import React from 'react';
import { Form, Row, Col, Button } from 'react-bootstrap';
import { FaPlus, FaPencilAlt, FaSave, FaTimes, FaUserCheck, FaShieldAlt, FaUnlockAlt, FaUserShield } from 'react-icons/fa';
import FormInput from '../../../../components/form/FormInput';
import FormSelect from '../../../../components/form/FormSelect';
import { parseIsReleased } from './TeamMemberTable';

const MODULE_LIST = [
  { key: 'Dashboard', label: 'Dashboard Overview', actions: ['View'] },
  { key: 'Team', label: 'Team Management', actions: ['View', 'Create', 'Edit'] }, 
  { key: 'Projects', label: 'Projects & Tasks', actions: ['View', 'Create', 'Edit'] }, 
  { key: 'Roles', label: 'Role Management', actions: ['View', 'Create', 'Edit'] }, 
  { key: 'Auditions', label: 'Auditions & Applications', actions: ['View', 'Action'] }, 
  { key: 'Reviews', label: 'Reviews', actions: ['View'] },
  { key: 'Meetings', label: 'Meetings', actions: ['View'] },
  { key: 'Billing', label: 'Billing & Payments', actions: ['View', 'Create', 'Edit'] }, 
  { key: 'Reports', label: 'Reports & Analytics', actions: ['View', 'Export'] }
];

const TeamMemberForm = ({ control, handleSubmit, setValue, watch, editIndex, lookupOptions, onCancel, onSubmitForm }) => {
  const watchedPermissions = watch('permissions') || [];
  const isLoginEnabled = Boolean(watch('enableLogin'));
  const rawIsActive = watch('isActive');
  const isActive = rawIsActive !== undefined && rawIsActive !== null ? Boolean(rawIsActive) : !parseIsReleased(watch('isReleased'));

  const handleCheckboxChange = (moduleKey, action) => {
    const permString = `${moduleKey}:${action}`;
    let currentPerms = [...watchedPermissions];
    if (currentPerms.includes(permString)) {
      currentPerms = currentPerms.filter(p => p !== permString);
    } else {
      currentPerms.push(permString);
    }
    setValue('permissions', currentPerms);
  };

  return (
    <div className="mtm-card-box mtm-form-card">
      <div className="mtm-form-header">
        <div className="d-flex align-items-center gap-2">
          <div className="mtm-form-header-icon">
            {editIndex !== null ? <FaPencilAlt /> : <FaPlus />}
          </div>
          <div>
            <h5 className="m-0 text-white fw-bold">
              {editIndex !== null ? 'Edit Member Details' : 'Add New Team Member'}
            </h5>
            <small className="text-muted d-none d-md-block">
              Configure staff account info, access login and permission matrix.
            </small>
          </div>
        </div>
        <button type="button" className="mtm-close-btn" title="Close Form" onClick={onCancel}>
          <FaTimes size={16} />
        </button>
      </div>

      <Form onSubmit={handleSubmit(onSubmitForm)} className="mtm-form">
        <div className="mtm-section-label">
          <span className="mtm-section-dot"></span> Core Information
        </div>
        
        <Row className="g-3 mb-4">
          <Col md={3} sm={6}><FormInput name="name" label="Full Name *" control={control} isRequired /></Col>
          <Col md={3} sm={6}><FormInput name="email" label="Email Address *" type="email" control={control} isRequired /></Col>
          <Col md={3} sm={6}><FormInput name="mobile" label="Mobile Number *" control={control} isRequired /></Col>
          <Col md={3} sm={6}><FormSelect name="designation" label="Designation *" control={control} options={lookupOptions.pocDesignation} isRequired /></Col>
          <Col md={3} sm={6}><FormSelect name="contactType" label="Contact Type *" control={control} options={lookupOptions.pocContactType} isRequired /></Col>

          {/* MEMBER STATUS TOGGLE PANEL: Switch ON = Active, Switch OFF = Released */}
          <Col md={3} sm={6}>
            <Form.Label className="mtm-field-label">Member Status</Form.Label>
            <div className={`mtm-toggle-panel ${isActive ? 'panel-active' : 'panel-released'}`}>
              <div className="d-flex align-items-center justify-content-between h-100">
                <div className="d-flex align-items-center gap-2">
                  <div className="mtm-toggle-icon">
                    {isActive ? <FaUserCheck color="#10b981" size={13} /> : <FaUnlockAlt color="#f59e0b" size={13} />}
                  </div>
                  <div>
                    <h6 className="m-0 text-white fw-bold" style={{ fontSize: '12px' }}>Member Status</h6>
                    <p className="mtm-panel-desc m-0">{isActive ? "Active" : "Released"}</p>
                  </div>
                </div>
                <Form.Check 
                  type="switch" 
                  id="releaseCheck" 
                  checked={isActive} 
                  onChange={(e) => {
                    const active = e.target.checked;
                    setValue('isActive', active);
                    setValue('isReleased', active ? 0 : 1);
                    setValue('is_released', active ? 0 : 1);
                  }} 
                  className="mtm-custom-switch" 
                />
              </div>
            </div>
          </Col>

          {/* USER LOGIN ACCESS TOGGLE PANEL */}
          <Col md={3} sm={6}>
            <Form.Label className="mtm-field-label">User Access</Form.Label>
            <div className="mtm-toggle-panel panel-dark">
              <div className="d-flex align-items-center justify-content-between h-100">
                <div className="d-flex align-items-center gap-2">
                  <div className="mtm-toggle-icon">
                    <FaUserShield color="#E10600" size={13} />
                  </div>
                  <div>
                    <h6 className="m-0 text-white fw-bold" style={{ fontSize: '12px' }}>Login Access</h6>
                    <p className="mtm-panel-desc m-0">{isLoginEnabled ? "Enabled" : "Disabled"}</p>
                  </div>
                </div>
                <Form.Check 
                  type="switch" 
                  id="loginCheck" 
                  checked={isLoginEnabled} 
                  onChange={(e) => setValue('enableLogin', e.target.checked)} 
                  className="mtm-custom-switch" 
                />
              </div>
            </div>
          </Col>

          {/* ACCOUNT PASSWORD INPUT */}
          {isLoginEnabled && (
            <Col md={3} sm={6}>
              <FormInput name="password" label="Account Password *" type="password" placeholder="Staff@123" control={control} />
            </Col>
          )}
        </Row>

        {/* MODULE ACCESS MATRIX */}
        <div className="mtm-section-label mt-4">
          <span className="mtm-section-dot"></span> <FaShieldAlt className="me-1"/> Module Access Matrix
        </div>
        
        <div className="mtm-matrix-container mb-4">
          <div className="mtm-matrix-header d-none d-md-flex">
            <div className="mtm-h-col-name">MODULE NAME</div>
            <div className="mtm-h-col-perms">ACTION PERMISSIONS</div>
          </div>

          {MODULE_LIST.map((mod) => (
            <div className="mtm-matrix-row" key={mod.key}>
              <div className="mtm-row-label">{mod.label}</div>
              <div className="mtm-row-controls">
                {mod.actions.map(act => (
                  <Form.Check 
                    key={act} 
                    type="checkbox" 
                    label={act} 
                    id={`chk_${mod.key}_${act}`}
                    checked={watchedPermissions.includes(`${mod.key}:${act}`)}
                    onChange={() => handleCheckboxChange(mod.key, act)}
                    className="mtm-checkbox-custom"
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* BOTTOM ACTION BUTTONS - SINGLE ROW ON MOBILE */}
        <div className="mtm-form-actions">
          <Button type="button" className="mtm-btn-outline" onClick={onCancel}>
            <span className="d-none d-md-inline">Discard Changes</span>
            <span className="d-md-none">Discard</span>
          </Button>
          <Button className="mtm-btn-primary" type="submit">
            <FaSave /> 
            <span className="d-none d-md-inline">Save Team Member</span>
            <span className="d-md-none">Save</span>
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default TeamMemberForm;
